












































































































































































































































































import { cloneDeep } from 'lodash';
import { computed, defineComponent, provide, ref } from '@vue/composition-api';

import {
  WorkflowFormTemplate,
  IWorkflowFormTemplate,
  IWorkflowFormTemplateRevisionData,
  ISystemError,
  SystemError,
  IWorkflowFormTemplateConfigHeaderData,
  IWorkflowFormTemplateConfigHeader,
  IWorkflowFormTemplateRevision,
  WorkflowFormTemplateConfigHeaderTypeEnum
} from '@/models';

import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';

import mixins from '@/utils/mixins';
import auth from '@/mixins/auth';
import snackbarMixin from '@/mixins/snackbar';
import draggable from '@/mixins/draggable';

import VueDraggable from 'vuedraggable';
import { ALForm } from '@/common/component.types';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue';

import { DataTableHeader } from 'vuetify';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';
import {
  IWorkflowFormTemplateRevisionActions,
  IWorkflowFormTemplateRevisionGetters,
  IWorkflowFormTemplateRevisionMutations,
  IWorkflowFormTemplateState
} from '@/store/workflow-form-template';
import { requiredValidator } from '@/utils/validators';
import { SelectedWorkflowFormFieldKey } from './tabs/WorkflowFormBuilderCalculationTab.vue';
import WorkflowFormBuilderFieldMetaLabelAutoPicker from './computation/WorkflowFormBuilderFieldMetaLabelAutoPicker.vue';

const baseMixins = mixins(auth, draggable, snackbarMixin);

export default defineComponent({
  name: 'WorkflowFormBuilderMetaLabelColumnsDialog',

  components: {
    ALDialog,
    ErrorAlert,
    NoDataAvailablePlaceholder,
    VueDraggable,
    WorkflowFormBuilderFieldMetaLabelAutoPicker
  },

  mixins: [baseMixins],

  setup(props, ctx) {
    const dialog = ref(false);
    const isFormValid = ref(false);

    const isDragging = ref(false);
    const loading = ref(false);

    const form = ref<ALForm>();

    const error = ref<ISystemError | null>(null);

    const templateData = ref({} as IWorkflowFormTemplate);

    const templateRevisionData = ref<IWorkflowFormTemplateRevisionData>({});

    const currentTab = ref<WorkflowFormTemplateConfigHeaderTypeEnum>(
      WorkflowFormTemplateConfigHeaderTypeEnum.Table
    );

    function close() {
      dialog.value = false;
    }

    function addConfigHeader(type: number) {
      const headersLength = templateData.value.config!.headers!.length;
      templateData.value.config!.headers!.push({
        meta_label: '',
        display_label: '',
        order: headersLength + 1,
        width: 0,
        type: type
      });
    }

    function clearAllErrors() {
      error.value = null;
    }

    function resetFormValidation(): void {
      if (form.value) {
        return form.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (form.value) {
        valid = form.value.validate();
      }
      return valid;
    }

    function open(revision: IWorkflowFormTemplateRevision) {
      clearAllErrors();

      resetFormValidation();

      templateData.value = cloneDeep(revision.template!);
      templateRevisionData.value = cloneDeep(revision);

      dialog.value = true;
    }

    const revisionId = computed(() => templateRevisionData.value.id);

    provide(SelectedWorkflowFormFieldKey, {
      revisionId: revisionId
    });

    const proxyTemplateConfigHeaders = computed({
      get(): IWorkflowFormTemplateConfigHeader[] {
        return templateData.value.config
          ? templateData.value.config.headers.filter(
              header => header.type === (currentTab.value as number)
            )!
          : [];
      },
      set(value: IWorkflowFormTemplateConfigHeader[]) {
        templateData.value.config!.headers = value;
      }
    });

    function updateHeaderOrder() {
      isDragging.value = false;

      clearAllErrors();

      // using proxyItems as the value itself is updated before function is called
      proxyTemplateConfigHeaders.value.map((item, index: number) => {
        if (item.order !== index + 1) {
          item.order = index + 1;
        }
        return item;
      });
    }

    function removeHeader(header: IWorkflowFormTemplateConfigHeader): void {
      const headerIndex = templateData.value.config!.headers.indexOf(header);

      if (headerIndex > -1) {
        templateData.value.config!.headers.splice(headerIndex, 1);
      }
    }

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowFormTemplateState,
      IWorkflowFormTemplateRevisionGetters,
      IWorkflowFormTemplateRevisionActions,
      IWorkflowFormTemplateRevisionMutations
    >(store, 'workflowFormTemplate');

    const { updateWorkflowFormTemplate } = useActions([
      'updateWorkflowFormTemplate'
    ]);

    const { getWorkflowFormTemplateById } = useGetters([
      'getWorkflowFormTemplateById'
    ]);

    function saveTemplate() {
      clearAllErrors();
      if (validateForm()) {
        let templateInstance: IWorkflowFormTemplate | null = null;

        try {
          templateInstance = new WorkflowFormTemplate(templateData.value);
        } catch (err) {
          if (SystemError.isSystemError(err)) {
            error.value = err;
          }
        }
        if (templateInstance != null) {
          loading.value = true;

          // Only diffDeep the config because the form is designed to update only the config property of the template instance.
          const payloadConfig = diffDeep(
            templateInstance.config,
            getWorkflowFormTemplateById.value(templateInstance.id!)!.config,
            true
          );

          updateWorkflowFormTemplate({
            id: templateInstance.id!,
            config: payloadConfig
          })
            .then(() => {
              ctx.emit('saved');
              close();
            })
            .catch(error => {
              error.value = setErrorFromAxiosError(error);
            })
            .finally(() => {
              loading.value = false;
            });
        }
      }
    }

    const tableHeaders = computed(() => {
      const actionColumn: DataTableHeader<IWorkflowFormTemplateConfigHeaderData> = {
        text: '',
        align: 'center',
        sortable: false,
        value: 'actions',
        width: '30'
      };

      const widthColumn: DataTableHeader<IWorkflowFormTemplateConfigHeaderData> = {
        text: 'Width',
        align: 'start',
        sortable: false,
        value: 'width',
        width: '150'
      };

      const headers: DataTableHeader<
        IWorkflowFormTemplateConfigHeaderData
      >[] = [
        {
          text: '',
          value: 'drag',
          align: 'start',
          sortable: false,
          class: 'shrink',
          width: '20'
        },
        {
          text: 'Display Label',
          align: 'start',
          sortable: false,
          value: 'display_label'
        },
        {
          text: 'Meta Label',
          align: 'start',
          sortable: false,
          value: 'meta_label'
        }
      ];

      if (currentTab.value === WorkflowFormTemplateConfigHeaderTypeEnum.Table) {
        headers.push(widthColumn);
      }

      headers.push(actionColumn);

      return headers;
    });

    return {
      currentTab,
      WorkflowFormTemplateConfigHeaderTypeEnum,
      form,
      dialog,
      loading,
      error,
      addConfigHeader,
      open,
      isFormValid,
      tableHeaders,
      removeHeader,
      proxyTemplateConfigHeaders,
      updateHeaderOrder,
      clearAllErrors,
      close,
      saveTemplate,
      requiredValidator
    };
  }
});
